import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthenticatedFetch from './useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';

const normalizeBaseUrl = (raw) => {
  if (!raw) return '';
  return String(raw).trim().replace(/\/+$/, '');
};

const ensureApiPrefix = (path) => {
  const p = String(path || '');
  if (!p.startsWith('/')) return `/api/${p}`;
  return p.startsWith('/api/') || p === '/api' ? p : `/api${p}`;
};

const buildApiUrl = (endpoint) => {
  const base = normalizeBaseUrl(API_CONFIG.BASE_URL);
  const path = ensureApiPrefix(endpoint);

  if (!base) return path;

  const baseNoApi = base.endsWith('/api') ? base.slice(0, -4) : base;

  if (base.startsWith('/')) {
    if (base === '/api' && path.startsWith('/api')) return path;
    return `${baseNoApi}${path}`;
  }

  return `${baseNoApi}${path}`;
};

const apiRequest = async (authenticatedFetch, endpoint) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

const apiRequestJsonWithMethod = async (authenticatedFetch, endpoint, method, body) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl, {
    method,
    body: JSON.stringify(body || {}),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

const apiRequestWithMethod = async (authenticatedFetch, endpoint, method) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl, { method });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) return response.json();
  return response.text();
};

const toPositiveIntOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
};

export const useWbsTemplates = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['wbsTemplates'],
    queryFn: () => apiRequest(authenticatedFetch, '/wbs/templates'),
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

export const useCreateWbsTemplate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJsonWithMethod(authenticatedFetch, '/wbs/templates', 'POST', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['wbsTemplates'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateWbsTemplate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ templateId, payload }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/wbs/templates/${templateId}`, 'PUT', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['wbsTemplates'] });
      if (variables?.templateId != null) {
        await queryClient.invalidateQueries({ queryKey: ['wbsTemplateItems', variables.templateId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useCloneWbsTemplate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ templateId, payload }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/wbs/templates/${templateId}/clone`, 'POST', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['wbsTemplates'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteWbsTemplate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (templateId) => apiRequestWithMethod(authenticatedFetch, `/wbs/templates/${templateId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['wbsTemplates'] });
      if (variables != null) {
        await queryClient.removeQueries({ queryKey: ['wbsTemplateItems', variables] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useWbsTemplateItems = (templateId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;
  const normalizedTemplateId = toPositiveIntOrNull(templateId);

  return useQuery({
    queryKey: ['wbsTemplateItems', normalizedTemplateId],
    queryFn: () => apiRequest(authenticatedFetch, `/wbs/templates/${normalizedTemplateId}/items`),
    enabled: (optionsEnabled ?? true) && normalizedTemplateId !== null,
    ...restOptions,
  });
};

export const useCreateWbsTemplateItem = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ templateId, payload }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/wbs/templates/${templateId}/items`, 'POST', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['wbsTemplates'] });
      if (variables?.templateId != null) {
        await queryClient.invalidateQueries({ queryKey: ['wbsTemplateItems', variables.templateId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateWbsTemplateItem = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ templateId, itemId, payload }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/wbs/templates/${templateId}/items/${itemId}`, 'PUT', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['wbsTemplates'] });
      if (variables?.templateId != null) {
        await queryClient.invalidateQueries({ queryKey: ['wbsTemplateItems', variables.templateId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteWbsTemplateItem = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ templateId, itemId }) =>
      apiRequestWithMethod(authenticatedFetch, `/wbs/templates/${templateId}/items/${itemId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['wbsTemplates'] });
      if (variables?.templateId != null) {
        await queryClient.invalidateQueries({ queryKey: ['wbsTemplateItems', variables.templateId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useReorderWbsTemplateItems = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ templateId, itemIds }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/wbs/templates/${templateId}/items/reorder`, 'PUT', { itemIds }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['wbsTemplates'] });
      if (variables?.templateId != null) {
        await queryClient.invalidateQueries({ queryKey: ['wbsTemplateItems', variables.templateId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};